import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { OeLocationService } from '@app/oe-location.service';
import { PpApiGatewayService } from '@app/pp-api-gateway.service';
import { _ } from '@app/translation-marker';
import { Invoice } from '@app/types/invoice';
import { SessionStorage } from '@app/types/session-storage.enum';
import { WarningDialogComponent } from '@app/warning-dialog/warning-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { FileSaverService } from 'ngx-filesaver';
import { Observable, of, zip } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

enum WarningCountry {
  Venezuela = 'VE',
  UnitedStates = 'US',
  PuertoRico = 'PR', // The backend stores Puerto Rico as its own country, rather than a US state
}

enum WarningState {
  NewMexico = 'New Mexico',
  Pennsylvania = 'Pennsylvania',
  SouthCarolina = 'South Carolina',
  SouthDakota = 'South Dakota',
  Tennessee = 'Tennessee',
  Washington = 'Washington',
  PuertoRico = 'Puerto Rico',
}

@Component({
  selector: 'pp-invoice-download-button',
  templateUrl: './invoice-download-button.component.html',
  styleUrls: ['./invoice-download-button.component.scss'],
})
export class InvoiceDownloadButtonComponent implements OnInit {
  @Input() invoice: Invoice;
  xhrInProgress = false;

  constructor(
    private oeLocationService: OeLocationService,
    private ppApiGateway: PpApiGatewayService,
    private translate: TranslateService,
    private fileSaverService: FileSaverService,
    public dialog: MatDialog
  ) {}

  ngOnInit() {}

  downloadInvoice(event: Event, invoiceId: string) {
    event.stopPropagation();
    this.xhrInProgress = true;

    this.showWarningDialogOrNoop().subscribe(() => {
      this.ppApiGateway
        .getInvoicePdfByInvoiceId(invoiceId)
        .subscribe((pdfData) => {
          this.fileSaverService.save(pdfData, `${this.invoice.number}.pdf`);
          this.xhrInProgress = false;
        });
    });
  }

  showWarningDialogOrNoop(): Observable<any> {
    const hasPreviouslyViewedWarning =
      sessionStorage.getItem(SessionStorage.HasViewedWarning) === 'true';
    if (hasPreviouslyViewedWarning) {
      return of(null);
    }

    let translationSource;
    switch (this.invoice.billToContactCountry) {
      case WarningCountry.Venezuela:
        translationSource = this.getVenezuelaWarningDialogText();
        break;
      case WarningCountry.UnitedStates:
        if (
          !Object.values(WarningState).includes(this.invoice.billToContactState)
        ) {
          return of(null);
        }
        translationSource = this.getUnitedStatesWarningDialogText();
        break;
      case WarningCountry.PuertoRico:
        translationSource = this.getUnitedStatesWarningDialogText();
        break;
      default:
        return of(null);
    }

    return translationSource.pipe(
      mergeMap((translations) => {
        return this.dialog
          .open(WarningDialogComponent, {
            width: '325px',
            role: 'alertdialog',
            data: translations,
          })
          .afterClosed()
          .pipe(
            map(() => {
              sessionStorage.setItem(SessionStorage.HasViewedWarning, 'true');
            })
          );
      })
    );
  }

  getVenezuelaWarningDialogText() {
    return zip(
      this.translate.get(_('invoices.warning')),
      this.oeLocationService
        .getContactPhoneByCountryIsoCode(this.invoice.billToContactCountry)
        .pipe(
          mergeMap((contactPhone) => {
            return this.translate.get(
              _('invoices.ve_not_legal_invoice_warning'),
              { contactPhone }
            );
          })
        )
    ).pipe(
      map((translations) => {
        const [title, text] = translations;
        return { title, text };
      })
    );
  }

  getUnitedStatesWarningDialogText() {
    return zip(
      this.translate.get(_('invoices.warning')),
      this.translate.get(_('invoices.us_sales_tax_warning'))
    ).pipe(
      map((translations) => {
        const [title, text] = translations;
        return { title, text };
      })
    );
  }
}
